import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import MuiModal from '@mui/material/Modal';
import { useTheme } from '@mui/material/styles';
import { ReactNode, useMemo } from 'react';
import { SxProps, Theme, Breakpoint } from '@mui/material';
import { validateProps } from '../../../../utils/propValidator';

const propRules = {
  open: { type: 'boolean', required: true },
  onClose: { type: 'function', required: true },
  maxWidth: { oneOf: ['xs', 'sm', 'md', 'lg', 'xl', 'fit-content'] },
  isFullWidth: { type: 'boolean' },
  sx: { type: 'object' },
  children: { type: 'node' },
};

interface ModalWrapperProps {
  open: boolean;
  onClose: () => void;
  maxWidth?: Breakpoint | 'fit-content' | number | string;
  isFullWidth?: boolean;
  sx?: SxProps<Theme>;
  children?: ReactNode;
}

export default function ModalWrapper(props: ModalWrapperProps) {
  validateProps(props as unknown as Record<string, unknown>, propRules, 'ModalWrapper');
  const { open, onClose, maxWidth = 'sm', isFullWidth = false, sx = {}, children } = props;

  const theme = useTheme();
  const modalWidth = useMemo(() => {
    if (isFullWidth) return '90%';
    const breakpoint = theme.breakpoints.values[maxWidth as Breakpoint];
    if (breakpoint) return `${breakpoint}px`;
    if (typeof maxWidth === 'number') return `${maxWidth}px`;
    return maxWidth;
  }, [isFullWidth, maxWidth, theme.breakpoints.values]);

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 500 } }}
    >
      <Fade in={open}>
        <Box
          sx={(theme: Theme) => ({
            width: modalWidth,
            maxHeight: '90vh',
            maxWidth: `min(90vw, ${theme.breakpoints.values.lg}px)`,
            backgroundColor: 'background.default',
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            ...(sx as object),
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'auto',
          })}
        >
          {children}
        </Box>
      </Fade>
    </MuiModal>
  );
}
